"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { Link } from "@/navigation";

// 行业卡片配置接口
interface IndustryItem {
	src: string;
	labelKey: string;
	link?: string;
}

// "Aerospace
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/aerospace"
// "Automotive
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/automotive"
// "Automation
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/industry-equipment"
// "Medical Devices
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/medical-device"
// "Robotics
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/robotics-automation"
// "Consumer Products
// 选框点击后，跳转到：https://www.cncprotolabs.com/industry/consumer-electronics"

// 默认行业图片配置（6项，2x3网格）
const DEFAULT_INDUSTRIES: IndustryItem[] = [
	{
		src: "/image/new-version/machining/cnc-machining/industry/aerospace.webp",
		link: "/industry/aerospace",
		labelKey: "aerospace",
	},
	{
		src: "/image/new-version/machining/cnc-machining/industry/automotive.webp",
		link: "/industry/automotive",
		labelKey: "automotive",
	},
	{
		src: "/image/new-version/machining/cnc-machining/industry/automation.webp",
		link: "/industry/automation",
		labelKey: "automation",
	},
	{
		src: "/image/new-version/machining/cnc-machining/industry/medical-devices.webp",
		link: "/industry/medical-devices",
		labelKey: "medicalDevices",
	},
	{
		src: "/image/new-version/machining/cnc-machining/industry/robotics.webp",
		link: "/industry/robotics",
		labelKey: "robotics",
	},
	{
		src: "/image/new-version/machining/cnc-machining/industry/consumer-products.webp",
		link: "industry/consumer-goods",
		labelKey: "consumerProducts",
	},
];

// 组件 Props 接口
export interface IndustryProps {
	translationNamespace?: string;
	industries?: IndustryItem[];
	className?: string;
}

export default function Industry({
	translationNamespace = "cncIndustry",
	industries = DEFAULT_INDUSTRIES,
	className,
}: IndustryProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className={cn("w-full bg-[#c1e59d]", className)}>
			<div className="container py-8 sm:py-10 md:py-12 lg:py-16">
				{/* 两列布局：左侧文案 + 右侧图片网格 */}
				<div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-12">
					{/* 左侧文案区域 */}
					<div className="max-w-xl">
						{/* 主标题 */}
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>

						{/* 描述 */}
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>

					{/* 右侧图片网格 - 2x3卡片式布局 */}
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						{industries.map((industry, index) => {
							const cardClassName =
								"group flex flex-col rounded-xl bg-[#ededed] p-4 transition-colors duration-300 hover:bg-[#88d43c]";
							const cardContent = (
								<>
									{/* 左上角圆形图片 */}
									<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
										<Image
											src={industry.src}
											alt={t(industry.labelKey)}
											fill
											className="object-cover"
											sizes="80px"
										/>
									</div>
									{/* 右下角标签 */}
									<span className="mt-4 self-end text-right text-sm font-semibold text-[#1f1f1f] sm:text-base">
										{t(industry.labelKey)}
									</span>
								</>
							);

							if (industry.link) {
								return (
									<Link key={index} href={industry.link} className={cn(cardClassName, "cursor-pointer")}>
										{cardContent}
									</Link>
								);
							}

							return (
								<div key={index} className={cardClassName}>
									{cardContent}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</section>
	);
}
